/**
 * Композабл для роботи з HLS кешем через Service Worker
 */
export const useHLSCache = () => {
  const isSupported = ref(false)
  const isRegistered = ref(false)
  const cacheStats = ref({ count: 0, size: 0, items: [] })

  // Глобальний стан прогресу — доступний з будь-якого компонента
  const downloadProgress = useState('hlsDownloadProgress', () => ({}))

  // Автозбереження переглянутого в кеш (див. isAutoCacheEnabled у SW).
  // Джерело правди для інтерфейсу — localStorage, а SW отримує копію: його
  // кеш можуть стерти (очищення сайту, новий SW), і тоді налаштування має
  // доїхати туди знову з першою ж сторінкою
  const AUTO_CACHE_KEY = 'hls_auto_cache'
  const autoCache = useState('hlsAutoCache', () => true)

  const pushAutoCache = () => {
    navigator.serviceWorker?.getRegistration().then(reg => {
      reg?.active?.postMessage({ type: 'SET_AUTO_CACHE', enabled: autoCache.value })
    }).catch(() => {})
  }

  const setAutoCache = (enabled) => {
    autoCache.value = !!enabled
    try {
      localStorage.setItem(AUTO_CACHE_KEY, autoCache.value ? '1' : '0')
    } catch (e) {}
    pushAutoCache()
  }

  onMounted(() => {
    if (!import.meta.client || !('serviceWorker' in navigator)) return
    isSupported.value = true
    checkRegistration()

    try {
      autoCache.value = localStorage.getItem(AUTO_CACHE_KEY) !== '0'
    } catch (e) {}
    pushAutoCache()

    // Слухаємо повідомлення від SW
    navigator.serviceWorker.addEventListener('message', (event) => {
      const d = event.data

      if (d?.type === 'DOWNLOAD_PROGRESS_UPDATE') {
        const key = `${d.animeId}-${d.episodeId}`
        const existing = downloadProgress.value[key] || {}
        // Якщо вже скасовано — ігноруємо будь-які подальші оновлення від SW
        if (existing.status === 'cancelled') return
        downloadProgress.value = {
          ...downloadProgress.value,
          [key]: {
            ...existing,
            total: d.total,
            done: d.done,
            status: d.status,
            animeId: d.animeId,
            episodeId: d.episodeId,
            bytesDone: d.bytesDone ?? existing.bytesDone ?? 0,
            startTime: d.startTime ?? existing.startTime ?? Date.now(),
            // Зберігаємо metadata з SW (для відновлення після F5)
            animeTitle: d.animeTitle ?? existing.animeTitle,
            episodeTitle: d.episodeTitle ?? existing.episodeTitle,
            posterUrl: d.posterUrl ?? existing.posterUrl
          }
        }
        // Прибираємо скасовані одразу
        if (d.status === 'cancelled') {
          setTimeout(() => {
            const prog = { ...downloadProgress.value }
            delete prog[key]
            downloadProgress.value = prog
          }, 1500)
        }
      }
    })
  })

  const checkRegistration = async () => {
    try {
      const reg = await navigator.serviceWorker.getRegistration()
      isRegistered.value = !!reg
      return !!reg
    } catch { return false }
  }

  const register = async () => {
    if (!isSupported.value) return false
    try {
      // Перевіряємо чи вже є зареєстрований SW
      const existingReg = await navigator.serviceWorker.getRegistration('/')
      if (existingReg) {
        // console.log('[HLS Cache] Service Worker already registered')
        isRegistered.value = true
        return true
      }

      const reg = await navigator.serviceWorker.register('/sw-hls-v5.js', { scope: '/' })
      isRegistered.value = true
      // console.log('[HLS Cache] Service Worker registered')

      // Чекаємо активації без перезавантаження
      if (reg.installing) {
        await new Promise(resolve => {
          reg.installing.addEventListener('statechange', e => {
            if (e.target.state === 'activated') {
              // console.log('[HLS Cache] Service Worker activated')
              resolve()
            }
          })
        })
      }

      return true
    } catch (e) {
      console.error('[HLS Cache] register error:', e)
      return false
    }
  }

  // Відправка повідомлення з таймаутом
  const sendMessage = async (message, timeoutMs = 10000) => {
    try {
      const reg = await navigator.serviceWorker.getRegistration()
      if (!reg?.active) return null
      return new Promise((resolve) => {
        const ch = new MessageChannel()
        const timer = setTimeout(() => resolve(null), timeoutMs)
        ch.port1.onmessage = e => {
          clearTimeout(timer)
          resolve(e.data)
        }
        reg.active.postMessage(message, [ch.port2])
      })
    } catch (e) {
      console.error('[HLS Cache] sendMessage error:', e)
      return null
    }
  }

  // Встановлення контексту — fire-and-forget, не чекаємо відповіді
  const setEpisodeContext = (animeId, episodeId, animeSlug, token = null) => {
    if (!import.meta.client) return
    navigator.serviceWorker.getRegistration().then(reg => {
      if (reg?.active) {
        reg.active.postMessage({
          type: 'SET_EPISODE_CONTEXT',
          animeId: String(animeId),
          episodeId: String(episodeId),
          animeSlug: animeSlug || String(animeId),
          token: token || null
        })
      }
    }).catch(() => {})
  }

  const clearCache = async () => {
    // Видаляємо ВСІ кеші HLS (всі версії)
    try {
      const cacheNames = await caches.keys()
      const hlsCaches = cacheNames.filter(name =>
        name.startsWith('hls-cache-') || name.startsWith('hls-meta-')
      )
      await Promise.all(hlsCaches.map(name => caches.delete(name)))
      // console.log('[HLS Cache] Deleted all HLS caches:', hlsCaches)
    } catch (e) {
      // console.error('[HLS Cache] Error deleting caches:', e)
    }

    // Відправляємо повідомлення в SW для очищення його кешу
    const r = await sendMessage({ type: 'CLEAR_HLS_CACHE' })
    // console.log('[HLS Cache] Cache cleared successfully')

    return r?.success ?? true
  }

  const getCacheStats = async () => {
    const r = await sendMessage({ type: 'GET_CACHE_SIZE' })
    if (r) cacheStats.value = r
    return r
  }

  const getEpisodeStats = async () => {
    // Більший таймаут — може бути багато сегментів
    const r = await sendMessage({ type: 'GET_EPISODE_STATS' }, 30000)
    return r?.stats ?? null
  }

  // Завантаження — запускаємо і не чекаємо завершення (прогрес через broadcast)
  const downloadEpisode = (animeId, episodeId, animeSlug, hlsUrl, meta = {}) => {
    const key = `${animeId}-${episodeId}`
    downloadProgress.value = {
      ...downloadProgress.value,
      [key]: {
        total: 0, done: 0, status: 'fetching_playlist',
        animeId, episodeId,
        animeTitle: meta.animeTitle || animeSlug,
        episodeTitle: meta.episodeTitle || `Серія ${episodeId}`,
        posterUrl: meta.posterUrl || null,
        startTime: Date.now(),
        bytesDone: 0
      }
    }

    navigator.serviceWorker.getRegistration().then(reg => {
      if (!reg?.active) {
        downloadProgress.value = {
          ...downloadProgress.value,
          [key]: { ...downloadProgress.value[key], status: 'error' }
        }
        return
      }
      const ch = new MessageChannel()
      ch.port1.onmessage = (e) => {
        // Ігноруємо відповідь якщо вже скасовано
        const current = downloadProgress.value[key]
        if (current?.status === 'cancelled') return
        if (e.data?.success === false && !e.data?.cancelled) {
          downloadProgress.value = {
            ...downloadProgress.value,
            [key]: { ...downloadProgress.value[key], status: 'error' }
          }
        }
      }
      reg.active.postMessage({
        type: 'DOWNLOAD_EPISODE',
        animeId: String(animeId),
        episodeId: String(episodeId),
        animeSlug: animeSlug || String(animeId),
        hlsUrl,
        metadata: {
          animeTitle: meta.animeTitle,
          episodeTitle: meta.episodeTitle,
          posterUrl: meta.posterUrl
        }
      }, [ch.port2])
    }).catch(e => {
      console.error('[HLS Cache] download error:', e)
      downloadProgress.value = {
        ...downloadProgress.value,
        [key]: { ...downloadProgress.value[key], status: 'error' }
      }
    })
  }

  const cancelDownload = (animeId, episodeId) => {
    const key = `${animeId}-${episodeId}`
    // Оновлюємо стан одразу
    const existing = downloadProgress.value[key]
    if (existing) {
      downloadProgress.value = {
        ...downloadProgress.value,
        [key]: { ...existing, status: 'cancelled' }
      }
    }
    // Відправляємо в SW
    navigator.serviceWorker.getRegistration().then(reg => {
      if (reg?.active) {
        reg.active.postMessage({
          type: 'CANCEL_DOWNLOAD',
          animeId: String(animeId),
          episodeId: String(episodeId)
        })
      }
    }).catch(() => {})
    // Прибираємо через 1.5 сек
    setTimeout(() => {
      const prog = { ...downloadProgress.value }
      delete prog[key]
      downloadProgress.value = prog
    }, 1500)
  }

  const deleteEpisode = async (animeId, episodeId) => {
    const r = await sendMessage({ type: 'DELETE_EPISODE', animeId: String(animeId), episodeId: String(episodeId) })
    const prog = { ...downloadProgress.value }
    delete prog[`${animeId}-${episodeId}`]
    downloadProgress.value = prog
    return r?.success ?? false
  }

  const formatSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  const unregister = async () => {
    try {
      const reg = await navigator.serviceWorker.getRegistration()
      if (reg) { await reg.unregister(); isRegistered.value = false; return true }
      return false
    } catch { return false }
  }

  const forceUpdateSW = async () => {
    try {
      const reg = await navigator.serviceWorker.getRegistration()
      if (!reg) return false

      // console.log('[HLS Cache] Checking for SW updates...')
      await reg.update()

      // Якщо є новий SW в черзі - просто логуємо, НЕ активуємо
      if (reg.waiting) {
        // console.log('[HLS Cache] New SW is waiting. It will activate on next page load.')
        return true
      } else {
        // console.log('[HLS Cache] No updates available')
        return true
      }
    } catch (e) {
      console.error('[HLS Cache] Error checking SW update:', e)
      return false
    }
  }

  return {
    isSupported, isRegistered, cacheStats, downloadProgress,
    register, clearCache, getCacheStats, getEpisodeStats,
    setEpisodeContext, downloadEpisode, cancelDownload, deleteEpisode,
    formatSize, unregister, checkRegistration, forceUpdateSW,
    autoCache, setAutoCache
  }
}
